import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AccountStatus, investorState } from '../lib/types'
import {
  removeInvestorDataFromLS,
  setInvestorDataToSS,
  getInvestorDataFromSS,
  removeInvestorDataFromSS,
} from '../lib/utils/local-storage'

const getInitialData = () => {
  const { authToken, investorData } = getInvestorDataFromSS()
  if (!!authToken && !!investorData) {
    return {
      authToken: authToken,
      name: investorData.name,
      email: investorData.email,
      userId: investorData.userId,
      role: investorData.role,
      isEmailVerified: investorData.isEmailVerified,
      acceptedNDA: investorData.acceptedNDA,
      kycStatus: investorData.kycStatus,
      rememberMe: investorData.rememberMe,
      isActiveSession: investorData.isActiveSession,
      accountStatus: investorData.accountStatus,
      adminEmail: investorData.adminEmail,
      adminRole: investorData.adminRole,
      phone: investorData.phone,
      investorReference: investorData.investorReference,
      basicDetailsCompleted: investorData.basicDetailsCompleted ?? false,
      riskProfileCompleted: investorData.riskProfileCompleted ?? false,
      riskProfilerRequired: investorData.riskProfilerRequired ?? !investorData.riskProfileCompleted,
      onboardingDeferred: investorData.onboardingDeferred ?? false,
    }
  }

  return {
    authToken: '',
    name: '',
    email: '',
    userId: 0,
    role: '',
    isEmailVerified: false,
    acceptedNDA: false,
    kycStatus: '',
    rememberMe: false,
    isActiveSession: false,
    accountStatus: '',
    adminEmail: null,
    adminRole: null,
    phone: '',
    investorReference: '',
    basicDetailsCompleted: false,
    riskProfileCompleted: false,
    riskProfilerRequired: true,
    onboardingDeferred: false,
  }
}
const initialState = getInitialData()

const investorSlice = createSlice({
  name: 'investor',
  initialState,
  reducers: {
    createConfiguration: (state: investorState, action: PayloadAction<Partial<investorState>>) => {
      state.authToken = action.payload.authToken ?? ''
      state.name = action.payload.name ?? ''
      state.email = action.payload.email ?? ''
      state.userId = action.payload.userId ?? 0
      state.role = action.payload.role ?? ''
      state.isEmailVerified = action.payload.isEmailVerified ?? false
      state.acceptedNDA = action.payload.acceptedNDA ?? false
      state.kycStatus = action.payload.kycStatus ?? ''
      state.rememberMe = action.payload.rememberMe || false
      state.isActiveSession = action.payload.isActiveSession ?? false
      state.accountStatus = (action.payload.accountStatus ?? '') as AccountStatus
      state.adminEmail = action.payload.adminEmail ?? null
      state.adminRole = action.payload.adminRole ?? null
      state.phone = action.payload.phone ?? ''
      state.investorReference =
        action.payload.investorReference ??
        (action.payload as { investor_reference?: string }).investor_reference ??
        ''
      state.basicDetailsCompleted = action.payload.basicDetailsCompleted ?? false
      state.riskProfileCompleted = action.payload.riskProfileCompleted ?? false
      state.riskProfilerRequired =
        action.payload.riskProfilerRequired ?? !action.payload.riskProfileCompleted
      // Every new sign-in starts without the onboarding deferral.
      state.onboardingDeferred = action.payload.onboardingDeferred ?? false

      const { authToken, investorData } = getInvestorDataFromSS()
      if (!authToken && !investorData && action.payload.authToken && !action.payload.rememberMe)
        setInvestorDataToSS(state)
      return state
    },
    resetConfiguration: (state: investorState) => {
      if (state.rememberMe) removeInvestorDataFromLS()
      else removeInvestorDataFromSS()
      return initialState
    },
    updateConfiguration: (
      state: investorState,
      action: { payload: { name: keyof investorState; value: investorState[keyof investorState] } },
    ) => {
      const newState = {
        ...state,
        [action.payload.name]: action.payload.value,
      }
      setInvestorDataToSS(newState)
      return newState
    },
  },
})

export { investorSlice }
export const { createConfiguration, resetConfiguration, updateConfiguration } =
  investorSlice.actions

export default investorSlice.reducer
